/**
 * Transport for the bifrost websocket.
 *
 * This module owns the socket and nothing else: it knows about tickets,
 * reconnection and heartbeats, and knows nothing about Redux. That boundary is
 * deliberate. Today it opens one socket per tab; moving to one socket per
 * browser later means reimplementing this surface on top of a SharedWorker and
 * changing nothing above it.
 *
 * Public surface:
 *   connect({ userId, orgId })  open (or re-open) the socket
 *   disconnect()                close permanently; cancels retries in flight
 *   send(payload)               fire-and-forget; ignored when not open
 *   setFocus(workkardId)        declare the claim this tab is showing
 *   subscribe(listener)         returns an unsubscribe function
 *   getStatus()                 'disconnected' | 'connecting' | 'connected'
 */

import { BIFROST_HTTP_URL, BIFROST_WS_URL } from '../common/constants';
import { getAuthHeaders } from './authManager';
import { ensureFreshToken } from './tokenRefresh';

const BACKOFF_DELAYS = [1000, 2000, 4000, 8000, 16000, 32000, 60000];

// Bifrost expires a connection after connection.ttl (60s by default) and pings
// at half that. This is a belt-and-braces heartbeat from our side, comfortably
// inside the window even if a couple of server pings are lost.
const HEARTBEAT_MS = 25000;

const STATUS = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
};

let socket = null;
let status = STATUS.DISCONNECTED;
let identity = null;
let listeners = new Set();
let connectionEpoch = 0;
// True from entering open() until its socket exists, so two overlapping
// attempts cannot each buy a ticket.
let opening = false;

// The claim this tab currently shows. Bifrost holds focus in the memory of the
// pod serving the socket, so it does not survive a reconnect and has to be
// re-announced from onopen -- not only when the claim changes.
let focusedWorkkardId = 0;

let reconnectAttempts = 0;
let reconnectTimer = null;
let heartbeatTimer = null;
let ticketAbort = null;

// Deliberately not persisted. sessionStorage is copied when a tab is
// duplicated, and bifrost evicts an older connection holding the same session
// id -- so a persisted id would let a duplicated tab silently kill its
// original's socket. An in-memory id is stable for the page's lifetime, which
// is exactly as long as the socket needs to survive.
let sessionId = null;

const newSessionId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getSessionId = () => {
    if (!sessionId) sessionId = newSessionId();
    return sessionId;
};

const emit = (event) => {
    listeners.forEach((listener) => {
        try {
            listener(event);
        } catch (e) {
            console.error('[bifrost] listener threw', e);
        }
    });
};

const setStatus = (next) => {
    status = next;
};

const clearTimers = () => {
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    reconnectTimer = null;
    heartbeatTimer = null;
};

const cancelTicketRequest = () => {
    if (ticketAbort) {
        ticketAbort.abort();
        ticketAbort = null;
    }
};

/**
 * Tickets are single use with a 30 second lifetime, so every attempt -- the
 * first and every reconnect -- has to fetch a new one. Replaying a stored URL
 * fails permanently.
 */
const fetchTicket = async ({ userId, orgId }) => {
    await ensureFreshToken();

    // Scoped to this attempt rather than shared, so a later attempt cannot
    // overwrite the controller disconnect() needs to abort this one.
    const abort = new AbortController();
    ticketAbort = abort;

    const query = `user-id=${encodeURIComponent(userId)}`
        + `&org-id=${encodeURIComponent(orgId)}`
        + `&session-id=${encodeURIComponent(getSessionId())}`;

    const ticketUrl = `${BIFROST_HTTP_URL}/auth?${query}`;
    try {
        const response = await fetch(ticketUrl, {
            method: 'GET',
            headers: getAuthHeaders(),
            signal: abort.signal,
        });

        if (!response.ok) {
            console.error(`[bifrost] ticket request to ${ticketUrl} returned ${response.status}`);
            throw new Error(`bifrost ticket request failed with ${response.status}`);
        }
        const { ticket } = await response.json();
        if (!ticket) throw new Error('bifrost returned an empty ticket');
        return ticket;
    } finally {
        if (ticketAbort === abort) ticketAbort = null;
    }
};

const sendFocus = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ action: 'focus', workkardId: focusedWorkkardId }));
};

const startHeartbeat = (ws) => {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    const timer = setInterval(() => {
        // A heartbeat that outlived its socket clears itself: a stale interval
        // must never ping through a connection that has been replaced, nor keep
        // itself alive once that connection is gone.
        if (socket !== ws) {
            clearInterval(timer);
            if (heartbeatTimer === timer) heartbeatTimer = null;
            return;
        }
        if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
        }
    }, HEARTBEAT_MS);
    heartbeatTimer = timer;
};

const scheduleReconnect = (epoch) => {
    if (!identity || epoch !== connectionEpoch) return;

    const base = BACKOFF_DELAYS[Math.min(reconnectAttempts, BACKOFF_DELAYS.length - 1)];
    // Jitter keeps N tabs of the same browser from stampeding /auth in lockstep
    // after bifrost restarts.
    const delay = Math.round(base * (0.8 + Math.random() * 0.4));
    reconnectAttempts += 1;

    emit({ type: 'reconnecting', attempt: reconnectAttempts, delay });
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        open(epoch);
    }, delay);
};

const handleMessage = ({ data }) => {
    if (typeof data !== 'string') return;

    let envelope;
    try {
        envelope = JSON.parse(data);
    } catch (e) {
        // An older bifrost sent bare strings for its greeting and pong. Ignoring
        // them quietly is better than filling the console on every heartbeat.
        return;
    }
    if (!envelope || typeof envelope.event !== 'string') return;

    emit({ type: 'message', envelope });
};

const open = async (epoch) => {
    if (!identity || epoch !== connectionEpoch) return;
    // One socket per epoch. Two overlapping attempts would each buy a ticket and
    // connect under the same session id, and bifrost evicts the older connection
    // holding a session id -- so the second would kill the first, whose close
    // would schedule a third, and the tab would never hold a stable connection.
    if (socket || opening) return;

    opening = true;
    setStatus(STATUS.CONNECTING);
    let ticket;
    try {
        ticket = await fetchTicket(identity);
    } catch (error) {
        opening = false;
        if (error && error.name === 'AbortError') return; // disconnect() won the race
        if (!identity || epoch !== connectionEpoch) return; // superseded while in flight
        // A CORS rejection or a scheme the browser refuses surfaces here as an
        // opaque TypeError with no detail, so name the origin being called.
        console.error(`[bifrost] could not get a ticket from ${BIFROST_HTTP_URL}:`, error);
        setStatus(STATUS.DISCONNECTED);
        emit({ type: 'error', error: error && error.message });
        scheduleReconnect(epoch);
        return;
    }

    // disconnect(), or a connect() for another user, may have run while the
    // ticket request was outstanding.
    if (!identity || epoch !== connectionEpoch) {
        opening = false;
        return;
    }

    const query = `user-id=${encodeURIComponent(identity.userId)}`
        + `&org-id=${encodeURIComponent(identity.orgId)}`
        + `&session-id=${encodeURIComponent(getSessionId())}`
        + `&ticket=${encodeURIComponent(ticket)}`;

    const ws = new WebSocket(`${BIFROST_WS_URL}/socket?${query}`);
    socket = ws;
    opening = false;

    // Every handler below belongs to this socket alone. close() is synchronous
    // but onclose is not, so a handler can fire after its socket was abandoned
    // and a replacement opened; acting on the shared timers, status or reconnect
    // state from there would kill the live socket's heartbeat, report the tab as
    // disconnected, and schedule a reconnect nobody asked for.
    const isCurrent = () => epoch === connectionEpoch && socket === ws;

    ws.onopen = () => {
        if (!isCurrent()) return;
        reconnectAttempts = 0;
        setStatus(STATUS.CONNECTED);
        startHeartbeat(ws);
        // Before anything else: a tab that reconnected while sitting on a claim
        // is otherwise deaf to it until the user navigates away and back.
        if (focusedWorkkardId) sendFocus();
        emit({ type: 'open' });
    };

    ws.onmessage = (event) => {
        if (!isCurrent()) return;
        handleMessage(event);
    };

    ws.onerror = () => {
        if (!isCurrent()) return;
        // onclose always follows, and carries the information worth acting on.
        emit({ type: 'error', error: 'websocket error' });
    };

    ws.onclose = (event) => {
        if (!isCurrent()) return;
        // The close code separates the causes that otherwise look identical: a
        // rejected ticket, an ingress idle timeout, and a bifrost eviction.
        console.warn(
            `[bifrost] socket closed: code=${event && event.code} `
            + `reason=${(event && event.reason) || '(none)'} `
            + `clean=${!!(event && event.wasClean)}`
        );
        socket = null;
        if (heartbeatTimer) clearInterval(heartbeatTimer);
        heartbeatTimer = null;
        setStatus(STATUS.DISCONNECTED);
        emit({ type: 'close' });
        // identity is cleared by disconnect(), and scheduleReconnect() checks
        // it, so a deliberate close never schedules a retry.
        scheduleReconnect(epoch);
    };
};

export const connect = ({ userId, orgId }) => {
    if (!BIFROST_WS_URL) {
        console.warn(
            '[bifrost] bifrost_url is not configured in this build; live updates '
            + 'are off. Check that .env had bifrost_url set when the bundle was built.'
        );
        return;
    }
    if (!userId || !orgId) {
        console.warn('[bifrost] connect requires a user id and an org id');
        return;
    }

    const next = { userId: String(userId), orgId: String(orgId) };

    // Idempotent for the signed-in user. connect() is dispatched from every
    // place that refreshes the session context -- the header mounting, the auth
    // callback, saving a profile, and a user.* event arriving on this very
    // socket -- so tearing a live connection down to buy another ticket would
    // hit /auth once per dispatch and, because the replacement evicts its
    // predecessor, leave the tab deaf to the events it reconnected for.
    if (identity && identity.userId === next.userId && identity.orgId === next.orgId) {
        if (socket || opening || reconnectTimer) return;
        // Identity intact but nothing in flight: connect now rather than wait.
        reconnectAttempts = 0;
        open(connectionEpoch);
        return;
    }

    disconnect();
    identity = next;
    reconnectAttempts = 0;
    open(connectionEpoch);
};

export const disconnect = () => {
    // Ordered so nothing can resurrect the socket: drop the identity and bump
    // the epoch first, because the reconnect scheduler, the in-flight ticket
    // handler and every socket handler check them before proceeding.
    identity = null;
    connectionEpoch += 1;
    opening = false;
    clearTimers();
    cancelTicketRequest();

    if (socket) {
        const closing = socket;
        socket = null;
        try {
            closing.close();
        } catch (e) {
            /* already gone */
        }
    }
    setStatus(STATUS.DISCONNECTED);
};

/**
 * Declares the claim this tab is showing, so events on it are delivered even
 * when the signed-in user has no association with the claim. Pass 0 on leaving.
 *
 * Deliberately not cleared by disconnect(): a dropped socket does not mean the
 * user stopped looking at the claim, and the focus is re-sent on reconnect.
 */
export const setFocus = (workkardId) => {
    const next = Number(workkardId) || 0;
    if (next === focusedWorkkardId) return;
    focusedWorkkardId = next;
    sendFocus();
};

export const send = (payload) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(typeof payload === 'string' ? payload : JSON.stringify(payload));
    return true;
};

export const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const getStatus = () => status;

// Exposed for tests only.
export const __reset = () => {
    disconnect();
    listeners = new Set();
    sessionId = null;
    focusedWorkkardId = 0;
};
